import React, { useEffect, useState } from "react";
import { Box, Typography, CircularProgress, alpha } from "@mui/material";
import {
  CheckCircleRounded as CheckCircleIcon,
  ErrorOutlineRounded as ErrorOutlineIcon,
} from "@mui/icons-material";

import { COLORS, TYPO } from "../../styles/uiConstants";

const LABELS = {
  saving: "Kaydediliyor…",
  saved: "Kaydedildi",
  error: "Kaydedilemedi",
};

// "Kaydedildi" bu süreden sonra kaybolur; hata kalıcıdır.
const SAVED_VISIBLE_MS = 2500;

/**
 * SaveStatusIndicator — otomatik kayıt durumu. "Kaydet" butonu olmayan
 * autosave akışlarında kullanılır (TreatmentEditor, PersonalInfoTab vb.).
 *
 * Sade rozet: beyaz zemin + ince çerçeve, durum rengi yalnız ikon ve yazıda.
 * "Kaydedildi" kısa süre görünüp kaybolur.
 *
 * Varsayılan (yüzen): kaydırılabilir kapsayıcının sağ üstünde sticky durur,
 * height:0 olduğu için içeriği itmez; kenardan ve scroll çubuğundan boşluk bırakır.
 * `inline`: akış içinde (ör. editör başlık satırında) yer kaplayarak durur.
 *
 * @param {"idle"|"saving"|"saved"|"error"} status
 * @param {object} [palette]  Klinik tema paleti (saving rengi için)
 * @param {boolean} [inline=false]
 */
export default function SaveStatusIndicator({ status, palette, inline = false }) {
  const [savedVisible, setSavedVisible] = useState(false);

  useEffect(() => {
    if (status !== "saved") {
      setSavedVisible(false);
      return undefined;
    }
    setSavedVisible(true);
    const t = setTimeout(() => setSavedVisible(false), SAVED_VISIBLE_MS);
    return () => clearTimeout(t);
  }, [status]);

  const label = LABELS[status];
  const visible = Boolean(label) && (status !== "saved" || savedVisible);
  if (!visible) return null;

  const accent =
    status === "error"
      ? COLORS.errorDark
      : status === "saved"
        ? COLORS.successDark
        : palette?.primary || COLORS.slate600;

  const badge = (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.25,
        py: 0.5,
        borderRadius: 999,
        bgcolor: COLORS.white,
        border: `1px solid ${alpha(accent, 0.25)}`,
        boxShadow: inline ? "none" : "0 2px 8px rgba(15,23,42,0.08)",
        whiteSpace: "nowrap",
        animation: "pupillSaveIn .18s ease-out",
        "@keyframes pupillSaveIn": {
          from: { opacity: 0, transform: "translateY(-2px)" },
          to: { opacity: 1, transform: "none" },
        },
        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
      }}
    >
      {status === "saving" && (
        <CircularProgress size={12} thickness={6} sx={{ color: accent }} />
      )}
      {status === "saved" && <CheckCircleIcon sx={{ fontSize: 15, color: accent }} />}
      {status === "error" && <ErrorOutlineIcon sx={{ fontSize: 15, color: accent }} />}
      <Typography sx={{ fontSize: TYPO.label, fontWeight: 600, color: accent }}>
        {label}
      </Typography>
    </Box>
  );

  if (inline) return badge;

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        height: 0,
        overflow: "visible",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-end",
        pointerEvents: "none",
      }}
    >
      <Box sx={{ mt: 1.5, mr: 2 }}>{badge}</Box>
    </Box>
  );
}
